import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Avatar, Tooltip, Collapse, Chip, Divider, Link } from '@mui/material';
import {
  ChevronRight,
  ExpandMore as ExpandMoreIcon,
  Sync as SyncIcon,
  CloudDone as SyncedIcon,
  EmailOutlined as EmailIcon,
  PhoneOutlined as PhoneIcon,
  Phone as PhoneFilledIcon,
  Email as EmailFilledIcon,
  Badge as BadgeIcon,
  Business as PracticeIcon,
  Layers as FloorIcon,
  LocationOn as LocationIcon,
  Schedule as TimeIcon,
  Person as PersonIcon,
  MeetingRoom as CubeIcon,
  CheckCircle as AccountedIcon,
  PersonOff as NotAccountedIcon,
  HourglassEmpty as NotCheckedIcon,
} from '@mui/icons-material';
import { appColors } from '../../Config/Theme';
import { IncidentRosterPerson, PersonStatus } from '../../Types/Incident';
import StatusActionGroup from './StatusActionGroup';
import FinalStatusChip from './FinalStatusChip';
import PersonTypeChip from '../Elements/PersonTypeChip';
import RosterSourceChip from '../Elements/RosterSourceChip';
import IncidentDayBadgeInfo from '../Elements/IncidentDayBadgeInfo';
import LobbyTrackContextLine from '../Elements/LobbyTrackContextLine';
import PhoneLink from '../Elements/PhoneLink';
import PersonMetadata from '../Elements/PersonMetadata';
import { formatTime } from '../../Utils/dateHelpers';
import { getPersonTypeLabel } from '../../Utils/personTypeLabels';
import { getInclusionReasonLabels } from '../../Utils/personHelpers';
import { getPersonStatusLabel, HEADCOUNT_STATUS_VISUALS } from '../../Utils/headcountStatus';

interface RosterPersonRowProps {
  person: IncidentRosterPerson;
  effectiveStatus: PersonStatus;
  isPending: boolean;
  onStatusChange: (personId: string, status: PersonStatus) => void;
  onRowClick?: (personId: string) => void;
  avatarColorIndex: number;
  readOnly?: boolean;
}

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const statusDisplay: Record<PersonStatus, { icon: React.ReactElement; color: string; bg: string }> = {
  accounted: { icon: <AccountedIcon sx={{ fontSize: 16 }} />, color: HEADCOUNT_STATUS_VISUALS.accounted.color, bg: HEADCOUNT_STATUS_VISUALS.accounted.bg },
  notAccounted: { icon: <NotAccountedIcon sx={{ fontSize: 16 }} />, color: HEADCOUNT_STATUS_VISUALS.notAccounted.color, bg: HEADCOUNT_STATUS_VISUALS.notAccounted.bg },
  notChecked: { icon: <NotCheckedIcon sx={{ fontSize: 16 }} />, color: HEADCOUNT_STATUS_VISUALS.notChecked.color, bg: HEADCOUNT_STATUS_VISUALS.notChecked.bg },
};

const getTelHref = (phone?: string | null): string | undefined => {
  if (!phone) return undefined;
  const stripped = phone.replace(/[\s()\-.\/]/g, '');
  return `tel:${stripped}`;
};
/** Returns true when the value is a human-readable label (not a raw number or blank). */
const isReadableLabel = (v?: string | number | null): v is string => {
  if (v == null) return false;
  const trimmed = String(v).trim();
  if (trimmed === '') return false;
  if (/^\d+$/.test(trimmed)) return false;
  return true;
};
const DetailItem: React.FC<{ icon: React.ReactNode; label: string; value?: string | null; href?: string }> = ({ icon, label, value, href }) => {
  if (!value) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
      <Box sx={{ color: appColors.text.caption, display: 'flex', flexShrink: 0 }}>{icon}</Box>
      <Typography variant="caption" sx={{ color: appColors.text.caption, minWidth: 70 }}>{label}</Typography>
      {href ? (
        <Link href={href} underline="hover" variant="body2" sx={{ color: appColors.primary.accent, fontWeight: 500, fontSize: '0.8125rem' }}>
          {value}
        </Link>
      ) : (
        <Typography variant="body2" sx={{ color: appColors.text.heading, fontWeight: 500, fontSize: '0.8125rem' }}>{value}</Typography>
      )}
    </Box>
  );
};

const RosterPersonRow: React.FC<RosterPersonRowProps> = ({
  person,
  effectiveStatus,
  isPending,
  onStatusChange,
  onRowClick,
  avatarColorIndex,
  readOnly = false,
}) => {
  const isNotAccounted = effectiveStatus === 'notAccounted';
  const [expanded, setExpanded] = useState(false);

  // Floor label for display
  const floorLabel = person.floor
    ? `Floor ${person.floor.replace(/^Floor\s+/i, '')}`
    : undefined;

  // Last updated text
  const lastUpdatedTime = (() => {
    if (!person.lastUpdatedAtUtc) return '';
    return formatTime(person.lastUpdatedAtUtc) || '';
  })();

  // Row highlight after status change
  const [highlighted, setHighlighted] = useState(false);
  const prevStatus = useRef(effectiveStatus);
  useEffect(() => {
    if (prevStatus.current !== effectiveStatus) {
      prevStatus.current = effectiveStatus;
      setHighlighted(true);
      const timer = setTimeout(() => setHighlighted(false), 800);
      return () => clearTimeout(timer);
    }
  }, [effectiveStatus]);

  // Sync tooltip
  const syncTooltip = isPending ? 'Pending sync' : person.lastUpdatedAtUtc ? 'Synced' : '';
  const syncColor = isPending ? appColors.status.warning : appColors.text.placeholder;

  return (
    <Box>
      {/* Main row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, sm: 1.5 },
          px: 2,
          py: 1.25,
          bgcolor: highlighted
            ? 'rgba(46, 139, 132, 0.08)'
            : appColors.surface.card,
          borderLeft: isNotAccounted ? `3px solid ${appColors.status.error}` : '3px solid transparent',
          borderBottom: expanded ? 'none' : `1px solid ${appColors.border.light}`,
          transition: 'background-color 0.4s ease',
          '&:hover': { bgcolor: isNotAccounted ? HEADCOUNT_STATUS_VISUALS.notAccounted.rowHoverBg : '#FAFAFA' },
          cursor: 'pointer',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
        }}
        onClick={() => setExpanded((prev) => !prev)}
    >
      {/* Avatar */}
      <Avatar
        sx={{
          width: 40,
          height: 40,
          bgcolor: appColors.avatarColors[avatarColorIndex % appColors.avatarColors.length],
          fontWeight: 600,
          fontSize: '0.85rem',
          flexShrink: 0,
        }}
      >
        {getInitials(person.displayName)}
      </Avatar>

      {/* Person info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Line 1: Name + Phone */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography
            variant="body2"
            noWrap
            title={person.displayName}
            sx={{ fontWeight: 600, color: appColors.text.heading, minWidth: 0 }}
          >
            {person.displayName}
          </Typography>
          {!expanded && person.phone && (
            <PhoneLink phone={person.phone} displayName={person.displayName} />
          )}
        </Box>
        {/* Lines 2-4: PersonMetadata (hidden when expanded) */}
        <PersonMetadata person={person} hidden={expanded}
        />
        {/* "Marked Not Accounted by..." context line (visible only when collapsed + notAccounted) */}
        {!expanded && isNotAccounted && person.lastUpdatedByName && (
          <Typography variant="caption" sx={{ color: appColors.status.error, fontWeight: 500, fontSize: '0.72rem', display: 'block', mt: 0.25 }}>
            Marked Not Accounted{person.lastUpdatedByName !== 'System' ? ` by ${person.lastUpdatedByName}` : ''}{lastUpdatedTime ? ` · ${lastUpdatedTime}` : ''}
          </Typography>
        )}
      </Box>

      {/* Status buttons or read-only chip */}
      <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
        {readOnly ? (
          <FinalStatusChip status={effectiveStatus} />
        ) : (
          <StatusActionGroup
            currentStatus={effectiveStatus}
            onStatusChange={(status) => onStatusChange(person.personId, status)}
          />
        )}
      </Box>

      {/* Updated time + sync */}
      <Box sx={{ flex: '0 0 100px', textAlign: 'right', display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
        {lastUpdatedTime && (
          <Typography variant="caption" sx={{ color: appColors.text.caption, fontSize: '0.72rem', lineHeight: 1.3, whiteSpace: 'nowrap' }}>
            Updated {lastUpdatedTime}
          </Typography>
        )}
        {!readOnly && (isPending || person.lastUpdatedAtUtc) && (
          <Tooltip title={isPending ? 'Changes not yet synced to server' : 'Status synced to server'} arrow>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              {isPending ? (
                <SyncIcon sx={{ fontSize: 14, color: syncColor }} />
              ) : (
                <SyncedIcon sx={{ fontSize: 14, color: syncColor }} />
              )}
              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: syncColor, fontWeight: 600 }}>
                {isPending ? 'Pending' : 'Synced'}
              </Typography>
            </Box>
          </Tooltip>
        )}
      </Box>

      {/* Expand/Collapse chevron */}
      <Box
        sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        onClick={(e) => { e.stopPropagation(); setExpanded((prev) => !prev); }}
      >
        {expanded ? (
          <ExpandMoreIcon sx={{ fontSize: 20, color: appColors.primary.main, transition: 'transform 0.2s' }} />
        ) : (
          <ChevronRight sx={{ fontSize: 20, color: appColors.text.placeholder, transition: 'transform 0.2s' }} />
        )}
      </Box>
      </Box>

      {/* Expandable detail panel */}
      <Collapse in={expanded} timeout={200}>
        <Box
          sx={{
            px: 2,
            pt: 1,
            pb: 1.5,
            bgcolor: expanded && isNotAccounted ? '#FEFAFA' : '#FAFBFC',
            borderBottom: `1px solid ${appColors.border.light}`,
            borderTop: `1px dashed ${appColors.border.default}`,
          }}
        >
          <Box sx={{ display: 'flex', gap: { xs: 2, md: 4 }, flexWrap: 'wrap', ml: { xs: 0, md: 6.5 } }}>
            {/* Contact details column */}
            <Box sx={{ minWidth: 220 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: appColors.text.caption, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5, display: 'block' }}>
                Contact
              </Typography>
              <DetailItem icon={<PersonIcon sx={{ fontSize: 16 }} />} label="Name" value={person.displayName} />
              <DetailItem
                icon={<EmailFilledIcon sx={{ fontSize: 16 }} />}
                label="Email"
                value={person.email}
                href={person.email ? `mailto:${person.email}` : undefined}
              />
              <DetailItem
                icon={<PhoneFilledIcon sx={{ fontSize: 16 }} />}
                label="Phone"
                value={person.phone}
                href={getTelHref(person.phone)}
              />
            </Box>

            {/* Location & role column */}
            <Box sx={{ minWidth: 200 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: appColors.text.caption, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5, display: 'block' }}>
                Details
              </Typography>
              <DetailItem icon={<BadgeIcon sx={{ fontSize: 16 }} />} label="Type" value={getPersonTypeLabel(person.personType)} />
              <DetailItem icon={<PracticeIcon sx={{ fontSize: 16 }} />} label="Company" value={person.companyName} />
              <DetailItem icon={<PracticeIcon sx={{ fontSize: 16 }} />} label="Practice" value={person.practice} />
              <DetailItem
                icon={<FloorIcon sx={{ fontSize: 16 }} />}
                label="Floor"
                value={person.floor ? `Floor ${person.floor.replace(/^Floor\s+/i, '')}` : undefined}
              />
              <DetailItem icon={<LocationIcon sx={{ fontSize: 16 }} />} label="Location" value={person.location} />              <DetailItem icon={<CubeIcon sx={{ fontSize: 16 }} />} label='Office' value={person.officeCubeNumber} />            </Box>

            {/* Status column */}
            <Box sx={{ minWidth: 180 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: appColors.text.caption, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5, display: 'block' }}>
                Status
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                <Chip
                  icon={statusDisplay[effectiveStatus].icon}
                  label={getPersonStatusLabel(effectiveStatus)}
                  size="small"
                  sx={{
                    bgcolor: statusDisplay[effectiveStatus].bg,
                    color: statusDisplay[effectiveStatus].color,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    border: `1px solid ${statusDisplay[effectiveStatus].color}`,
                    '& .MuiChip-icon': { color: statusDisplay[effectiveStatus].color },
                  }}
                />
              </Box>
              {lastUpdatedTime && (
                <DetailItem icon={<TimeIcon sx={{ fontSize: 16 }} />} label="Updated" value={lastUpdatedTime} />
              )}
              {person.lastUpdatedByName && person.lastUpdatedByName !== 'System' && (
                <DetailItem icon={<PersonIcon sx={{ fontSize: 16 }} />} label="By" value={person.lastUpdatedByName} />
              )}
              {isPending && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <SyncIcon sx={{ fontSize: 14, color: appColors.status.warning }} />
                  <Typography variant="caption" sx={{ color: appColors.status.warning, fontWeight: 600 }}>
                    Pending sync
                  </Typography>
                </Box>
              )}
              {person.isUnlisted && (
                <Box sx={{ mt: 0.5 }}>
                  <Chip label="Manual Add" size="small" sx={{ fontSize: '0.7rem', height: 20, bgcolor: appColors.status.warningBg, color: appColors.status.warning, fontWeight: 600 }} />
                </Box>
              )}
              {getInclusionReasonLabels(person).map((label) => (
                <Box key={label} sx={{ mt: 0.5 }}>
                  <RosterSourceChip rosterSource={label} />
                </Box>
              ))}
              {!person.lobbyTrackActivity && (person.lastIncidentDayBadgeActivity?.badgeActivityAtLocalDateTime || person.lastBadgeAtUtc !== undefined || person.lobbyTrackCheckInAtUtc || person.hasBadgeActivityToday !== undefined || person.rosterSource || person.isUnlisted || person.isAssignedToIncidentOffice) && (
                <Box sx={{ mt: 0.5 }}>
                  <IncidentDayBadgeInfo person={person} />
                </Box>
              )}
              {person.lobbyTrackActivity && (
                <Box sx={{ mt: 0.5 }}>
                  <LobbyTrackContextLine activity={person.lobbyTrackActivity} />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default React.memo(RosterPersonRow);
